/**
 * Lists, sections, and shopping mode.
 *
 * Authorised by: product-concept §8.2 (DECIDED: "groceries are the purest form of the
 * staleness/memory problem, and the founder's acute pain, so shopping gets a dedicated
 * mode rather than being 'just another list'"), §10.1 (shared lists — "primarily
 * shopping", which is what makes a list that is not a grocery list legitimate), §12
 * ("multiple shared lists"), and implementation-brief §2 (commutative list operations)
 * and §7 (push-plus-poll sync).
 *
 * Multiple lists and user-authored sections are RULINGS R124–R130. Read those before
 * changing anything here: R125 in particular is the ruling that widened this module's
 * central type, and it says what may and may not follow from that widening.
 *
 * LAWS:
 *   - **Route learning stays dead.** product-concept §8.2 carries an explicit founder
 *     decision note: automatic list ordering inferred from check-off history across
 *     visits was designed, critic-tested, and CUT. "Do not resurrect without new
 *     evidence." There is therefore no check-off history in this module, no visit
 *     record, and no per-item position learning. If you find yourself adding a field
 *     that would enable it, you are undoing a founder decision.
 *
 *     WHAT THIS MODULE CANNOT PROMISE, stated because the sentence above used to read as a
 *     system guarantee and is only true of this file. **The check-off history exists, in the
 *     product, permanently, and it cannot be removed.** Checking an item is `close()` on its
 *     Loop, which appends a `closed` event and a `closed` terminal record — each carrying `at`
 *     — to an append-only, never-pruned log; the item's section is stored beside it. So the
 *     device holds, for every item on every trip: which item, which section, and the
 *     millisecond it was ticked. Ordering by `at` within a session IS the check-off sequence;
 *     grouping by day IS the visit record. That is the complete training set for the cut
 *     feature, and it is a direct consequence of "nothing is ever taken away by time"
 *     (CLAUDE.md), which is not negotiable and outranks the convenience of not holding the data.
 *
 *     The law is therefore not "the history does not exist" but **"no code may order a list from
 *     it"**. `listGroups` below takes its order from exactly two inputs — the user's manual
 *     `sectionOrder`, and `addedAt` within a section — and from nothing else; its output is
 *     invariant under any permutation of the loops' terminal timestamps, which
 *     `packages/core/test/lists.test.ts` asserts directly. Recorded in docs/DEVIATIONS.md D21.
 *
 *     R125 restates this for the widened world: giving the user their own section ids widened
 *     the id TYPE and nothing else. It did not widen the inputs to the sort, and a section the
 *     user authored is ordered by exactly the same two inputs as a built-in one.
 *   - Section order is set manually, by the user, and persists. Never inferred.
 *   - Merge is a genuine order-independent join: commutative, associative and
 *     idempotent, asserted by property tests over generated replicas (brief §2 mandates
 *     property-based conflict tests). Concurrent adds both survive; deletes are
 *     tombstoned.
 *   - Nothing is destroyed. An item is tombstoned (`removedAt`), a section is tombstoned
 *     (`removedAt`), a list is archived (`archivedAt`). There is no hard delete in this
 *     module and there must not be one (CLAUDE.md: "History is append-only"; R130).
 */

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------

/**
 * The built-in section set: a typical UK supermarket walk, in walking order. This is a
 * starting order the user drags into their own (§8.2) — not a learned one, and not a
 * taxonomy we defend.
 *
 * THESE ARE IDS, NOT NAMES (RULINGS R22). They used to be twelve English strings in a
 * non-copy package, which CLAUDE.md forbids for user-visible text — "never inline a
 * user-visible string in a component", and twelve of them in a domain package is the same
 * violation with more distance. The display names live in `packages/copy` as `dept.*` keys in
 * both registers; every function below operates on ids and never on display strings, so a
 * register switch can never reorder a list.
 *
 * They are still called `dept.*` in the catalogue, and that is correct rather than legacy:
 * these twelve ARE supermarket departments. "Section" is the general concept (R125); a
 * department is what a section is called when the list is a grocery list.
 *
 * Stable, lowercase, hyphenated. Changing an id is a data migration; changing a display name
 * is a copy edit. That is the whole reason for the split.
 */
export const BUILT_IN_SECTIONS = [
  'produce',
  'bakery',
  'meat-fish',
  'dairy-eggs',
  'chilled',
  'frozen',
  'cupboard',
  'drinks',
  'household',
  'health-beauty',
  'pet-animal',
  'other',
] as const;

/**
 * The twelve, as a closed union.
 *
 * STILL CLOSED, and deliberately so after R125 widened its sibling. This union is what makes
 * `Record<BuiltInSectionId, CopyKey>` exhaustive in `@carn/ui`, so a thirteenth built-in is a
 * compile error until it has a name in both registers. That guarantee is the half of R22 worth
 * keeping and R125 keeps it: what widened is who may author a section, not whether Carn's own
 * twelve are enumerated.
 */
export type BuiltInSectionId = (typeof BUILT_IN_SECTIONS)[number];

/**
 * A section id. Opaque, stable, lowercase — and NEVER a display name (RULINGS R22, R125).
 *
 * `string`, not a union, because the user authors sections now (R125: "a section per person
 * inside one Gift ideas list is a strictly better shape than one list per person"). The
 * rename from `DepartmentId` is R125 executing this type's own previous instruction: *"If a
 * future ruling gives the user their own departments, widen this — and rename it, because a
 * widened type called `DepartmentId` would be a lie."*
 *
 * R22's law is not weakened by the widening — it is the only thing standing between a user
 * who names a section "Frozen" and a register switch reordering their list. An id is minted
 * (`mintSectionId`); a name is typed. They are never the same value.
 */
export type SectionId = string;

/** The catch-all. An item whose section is unknown is never hidden. */
export const UNSORTED_SECTION: SectionId = 'other';

/**
 * The prefix every user-authored section id carries.
 *
 * LOAD-BEARING, not decoration (R125 clause 4). A user who types "Frozen" must get a fresh
 * id, not the built-in `frozen` — otherwise their section silently inherits a catalogue name,
 * renaming it appears to do nothing, and switching to plain words renames a section the user
 * named. The prefix makes the two id spaces provably disjoint, because no member of
 * `BUILT_IN_SECTIONS` starts with it.
 */
export const CUSTOM_SECTION_PREFIX = 'sec-';

/**
 * A section the user authored.
 *
 * Built-in sections have NO row here — their names come from the catalogue, so there is
 * nothing per-list to store about them. Only sections the user made carry a definition, and
 * the thing that definition carries is the one thing Carn cannot know: what the user calls it.
 */
export type Section = {
  readonly id: SectionId;
  /**
   * The user's own words. USER CONTENT, NOT COPY, and therefore carrying no catalogue key and
   * no second register (R125 clause 3).
   *
   * The precedent is already shipped and already argued: `apps/web/src/list/SharedListPage.tsx`
   * renders a list's own name with the note "it is user content, not copy, so it carries no
   * catalogue key". A section name is the same kind of thing as a list name. The copy-law
   * linter governs strings Carn says; it has never governed strings the user says, and it must
   * not start — a linter that rejected "Mum" would be absurd.
   */
  readonly label: string;
  readonly addedAt: number;
  /** Per-field LWW clock for `label` (brief §2). */
  readonly updatedAt: number;
  /** Tombstone. Never a hard delete (R130). */
  readonly removedAt?: number;
};

/** Is this one of Carn's own twelve? */
export function isBuiltInSection(id: string): id is BuiltInSectionId {
  return (BUILT_IN_SECTIONS as readonly string[]).includes(id);
}

/**
 * Mint an id for a section the user just authored.
 *
 * `unique` is any per-device-unique string — the caller passes the same generator every other
 * id in the app uses. It is NEVER derived from the typed name: see `CUSTOM_SECTION_PREFIX` for
 * why a name-derived id is a defect rather than a nicety.
 */
export function mintSectionId(unique: string): SectionId {
  return `${CUSTOM_SECTION_PREFIX}${unique}`;
}

export function makeSection(args: { id: SectionId; label: string; now: number }): Section {
  return {
    id: args.id,
    label: args.label.trim(),
    addedAt: args.now,
    updatedAt: args.now,
  };
}

/**
 * Narrow a stored/synced string to a section id this list actually knows, falling back to the
 * catch-all.
 *
 * The boundary function for every layer that holds a section as text — a SQLite column, an
 * outbox payload, a shared-list POST from a browser. It never throws and never hides an item:
 * an unrecognised section lands in `UNSORTED_SECTION`, which `listGroups` always renders.
 *
 * THIS GOT MORE IMPORTANT WHEN THE TYPE WIDENED, not less (R125 clause 5). Before R125 the
 * only way to hold an unknown section was a corrupt row; now a perfectly ordinary sequence —
 * a partner removes a section on their device while you add an item to it on yours — produces
 * one. The item must still be visible when that happens, and this is the function that makes
 * sure it is.
 */
export function asSectionId(value: string | undefined | null, known?: Iterable<SectionId>): SectionId {
  if (value === undefined || value === null || value === '') return UNSORTED_SECTION;
  if (known === undefined) {
    // No list in hand: the honest answer is "any built-in, or a well-formed authored id".
    return isBuiltInSection(value) || value.startsWith(CUSTOM_SECTION_PREFIX)
      ? value
      : UNSORTED_SECTION;
  }
  for (const id of known) if (id === value) return value;
  return UNSORTED_SECTION;
}

// ---------------------------------------------------------------------------
// Items
// ---------------------------------------------------------------------------

/**
 * Who did a thing, and when. `at` and `by` live in ONE object so the pair cannot
 * desynchronise into the invalid state the critique found — `checkedBy` defined while
 * `checkedAt` was undefined.
 */
export type ActorMark = {
  readonly at: number;
  readonly by?: string;
};

export type ListItem = {
  readonly id: string;
  readonly listId: string;
  readonly name: string;
  /** Free text: "2 pints", "the blue one". Captured, never demanded. */
  readonly qualifier?: string;
  readonly section: SectionId;
  /**
   * The latest check and the latest uncheck. An item is checked when a check exists and
   * no LATER uncheck does (`isChecked`).
   *
   * Why both (critique: "uncheckItem cannot stick on a shared list"): the old rule took
   * the earliest non-undefined `checkedAt`, and unchecking merely cleared the local
   * value — so the next sync restored the remote check, permanently. The user tapped the
   * wrong row and the app undid their correction every time, forever, with no way to win.
   * With an explicit uncheck mark participating in the merge, the LATER of the two wins
   * per actor, so a correction sticks; and "an item checked by either party stays
   * checked" (brief §2) still holds unless somebody deliberately unchecked it afterwards.
   */
  readonly check?: ActorMark;
  readonly uncheck?: ActorMark;
  readonly addedAt: number;
  readonly addedBy?: string;
  /** Per-field LWW clock for name/qualifier/section (brief §2). */
  readonly updatedAt: number;
  /** Tombstone. Never a hard delete. */
  readonly removedAt?: number;
  /** How this item was caught — the fridge, the barn, the bathroom (§8.2). */
  readonly capturedPlaceLabel?: string;
};

export function makeItem(args: {
  id: string;
  listId: string;
  name: string;
  now: number;
  section?: SectionId;
  qualifier?: string;
  addedBy?: string;
  capturedPlaceLabel?: string;
}): ListItem {
  return {
    id: args.id,
    listId: args.listId,
    name: args.name,
    qualifier: args.qualifier,
    section: args.section ?? UNSORTED_SECTION,
    addedAt: args.now,
    addedBy: args.addedBy,
    updatedAt: args.now,
    capturedPlaceLabel: args.capturedPlaceLabel,
  };
}

export function isChecked(item: ListItem): boolean {
  if (!item.check) return false;
  if (!item.uncheck) return true;
  // A tie resolves to CHECKED: a false "checked" costs one glance at a shelf, a false
  // "unchecked" costs a forgotten item, and the shelf is cheaper.
  return item.uncheck.at <= item.check.at;
}

// ---------------------------------------------------------------------------
// Commutative operations
// ---------------------------------------------------------------------------

/**
 * Merge two views of the same item.
 *
 * Every rule below is a total, order-independent join, so `mergeItem` is commutative,
 * associative and idempotent — the property the module claimed and did not have
 * (`checkedBy` used to resolve same-millisecond ties toward the left argument, so
 * mergeItem(a,b) and mergeItem(b,a) disagreed).
 *
 *   - `check` / `uncheck`: the LATEST mark wins, ties broken by actor id, so two people
 *     acting in the same millisecond converge everywhere.
 *   - `name` / `qualifier` / `section` and the other descriptive fields: per-field LWW
 *     on `updatedAt`, ties broken by a total key over every field that is copied.
 *   - `addedAt`: earliest. `removedAt`: earliest non-undefined (a tombstone, once set,
 *     is a fact).
 */
export function mergeItem(a: ListItem, b: ListItem): ListItem {
  const winner = laterUpdate(a, b);

  return {
    ...winner,
    check: laterMark(a.check, b.check),
    uncheck: laterMark(a.uncheck, b.uncheck),
    addedAt: Math.min(a.addedAt, b.addedAt),
    updatedAt: Math.max(a.updatedAt, b.updatedAt),
    removedAt: earliest(a.removedAt, b.removedAt),
  };
}

function laterUpdate(a: ListItem, b: ListItem): ListItem {
  if (a.updatedAt !== b.updatedAt) return a.updatedAt > b.updatedAt ? a : b;
  // Same clock: order by a total, symmetric key so both replicas agree.
  const ka = itemTieKey(a);
  const kb = itemTieKey(b);
  if (ka !== kb) return ka > kb ? a : b;
  return a;
}

/**
 * A total, symmetric ordering key over EVERY field `mergeItem` copies from the winner.
 * It must cover all of them: when two replicas tie on `updatedAt` and on this key, the
 * merge falls back to "either one", and that is only sound if those fields are identical.
 * JSON encoding rather than concatenation, so two different items cannot produce one key.
 */
function itemTieKey(item: ListItem): string {
  return JSON.stringify([
    item.id,
    item.listId,
    item.name,
    item.qualifier ?? null,
    item.section,
    item.addedBy ?? null,
    item.capturedPlaceLabel ?? null,
  ]);
}

function laterMark(a: ActorMark | undefined, b: ActorMark | undefined): ActorMark | undefined {
  if (!a) return b;
  if (!b) return a;
  if (a.at !== b.at) return a.at > b.at ? a : b;
  const ka = a.by ?? '';
  const kb = b.by ?? '';
  if (ka !== kb) return ka > kb ? a : b;
  return a;
}

function earliest(a?: number, b?: number): number | undefined {
  if (a === undefined) return b;
  if (b === undefined) return a;
  return Math.min(a, b);
}

/** Concurrent adds both survive: union by id, merging collisions. */
export function mergeItems(
  local: readonly ListItem[],
  remote: readonly ListItem[],
): ListItem[] {
  const byId = new Map<string, ListItem>();
  for (const item of local) byId.set(item.id, item);
  for (const item of remote) {
    const existing = byId.get(item.id);
    byId.set(item.id, existing ? mergeItem(existing, item) : item);
  }
  return [...byId.values()];
}

/**
 * Check an item off. Idempotent: re-checking an already-checked item is a no-op, which
 * keeps merges stable.
 *
 * The timestamp is forced past any existing uncheck so the user's gesture wins locally
 * even if the device clock is behind the one that unchecked it. Aisle three beats
 * arithmetic.
 */
export function checkItem(item: ListItem, now: number, by?: string): ListItem {
  if (isChecked(item)) return item;
  const at = Math.max(now, (item.uncheck?.at ?? 0) + 1);
  return { ...item, check: { at, by } };
}

/**
 * Unchecking is a local correction and is permitted — the user tapped the wrong row, and
 * it must STICK (see `ListItem.check`). Same clock-forcing rule as `checkItem`.
 */
export function uncheckItem(item: ListItem, now: number, by?: string): ListItem {
  if (!isChecked(item)) return item;
  const at = Math.max(now, (item.check?.at ?? 0) + 1);
  return { ...item, uncheck: { at, by } };
}

export function removeItem(item: ListItem, now: number): ListItem {
  return { ...item, removedAt: item.removedAt ?? now };
}

/**
 * Move an item into a different section.
 *
 * The gesture R125 exists to serve: you captured "gardening book" at the kitchen table and
 * now you know it is for your mother. `updatedAt` advances so the move wins a merge against
 * a replica that has not seen it; nothing else about the item changes, and in particular a
 * checked item stays checked — where a thing sits and whether you have it are two questions.
 */
export function moveItemToSection(item: ListItem, section: SectionId, now: number): ListItem {
  if (item.section === section) return item;
  return { ...item, section, updatedAt: Math.max(now, item.updatedAt + 1) };
}

// ---------------------------------------------------------------------------
// Lists
// ---------------------------------------------------------------------------

/**
 * What kind of list this is. EXACTLY TWO MEMBERS, and R126 is the ruling that fixes the
 * count — the same way `Temperature` has exactly five and never gains a sixth.
 *
 *   - `shopping` — seeds the twelve built-in sections, may attach a store geofence, has a
 *     shopping mode, and vanishes checked items during a trip (§8.2).
 *   - `plain` — starts with no sections, offers no geofence, has no trip, and keeps checked
 *     items visible (R127). Gift ideas, packing, a hardware run.
 *
 * IMMUTABLE AFTER CREATION (R126). Deriving the kind from "does it have a store attached" was
 * considered and rejected: a user builds the list before they attach the shop, and a list that
 * silently changed kind on attachment would be the "smart rearrangement" design-direction §4
 * forbids.
 */
export const LIST_KINDS = ['shopping', 'plain'] as const;
export type ListKind = (typeof LIST_KINDS)[number];

export function isListKind(value: string): value is ListKind {
  return (LIST_KINDS as readonly string[]).includes(value);
}

export type CarnList = {
  readonly id: string;
  /**
   * The user's own words. USER CONTENT, NOT COPY — same rule and same reason as
   * `Section.label`, and the precedent this file cites there.
   */
  readonly name: string;
  /** Fixed at creation (R126). */
  readonly kind: ListKind;
  /**
   * The user's manual section order, as IDS. Set at creation from the kind's default, then
   * rearranged whenever they like (§8.2 says "once" about a supermarket walk, which is a
   * statement about how often a user WANTS to, not a cap the product imposes).
   *
   * Holds built-in ids and authored ids intermixed, because a list may legitimately have
   * both — a shopping list with an extra "Farm shop" section is exactly the case R125's
   * widening serves.
   */
  readonly sectionOrder: readonly SectionId[];
  /**
   * Definitions for the sections the USER authored. Built-in sections appear in
   * `sectionOrder` and have no entry here: their names come from the catalogue, so there is
   * nothing per-list to store.
   */
  readonly sections: readonly Section[];
  /**
   * The store this list's geofence is attached to. Evaluated ON-DEVICE ONLY — the
   * server never receives raw location (brief §3, privacy invariant).
   *
   * Only ever set on a `shopping` list (R126).
   */
  readonly storePlaceId?: string;
  readonly storeLabel?: string;
  /** Free tier is one list; Plus adds more (product-concept §12, R124). */
  readonly shared: boolean;
  readonly memberIds: readonly string[];
  readonly createdAt: number;
  /** Per-field LWW clock for name/sectionOrder/store fields (brief §2). */
  readonly updatedAt: number;
  /**
   * Archived, never deleted (R130). CLAUDE.md: "Nothing is ever taken away by time. History
   * is append-only." A list that hard-deleted its items would be the one place in Carn where
   * a tap destroys history.
   */
  readonly archivedAt?: number;
  /**
   * THE CLEAR WATERMARK, AND ITS COUNTERMARK (docs/DEVIATIONS.md LC1).
   *
   * §8.2's sentence is "checked items vanish; the list ends; the mode congratulates and closes."
   * `R127` ruled that every clause after the semicolon is about the TRIP, and the mode does
   * close itself — but nobody ever built "the list ends". A weekly shopping list therefore
   * carries every item ever bought: `Trolley` reads "247 in the trolley" after six months and
   * only ever grows, which is §8.3's backlog wall arriving on the one surface whose own header
   * cites §8.3 as its justification. `NC1` closed every automatic route to it (a list-bound loop
   * has no temperature, so it cannot cool, compost, or reach a review), so only a user act can
   * discharge it.
   *
   * A CHECK MARK AND A COUNTERMARK, WHICH IS `ListItem.check`/`uncheck`'S EXACT SHAPE, and it is
   * copied deliberately rather than invented. `cleared.at` is a watermark: an item whose latest
   * check is at or before it is PUT AWAY — drawn nowhere, counted nowhere. A later `uncleared`
   * cancels it, which is the undo. That pattern already merges correctly, already ties to the
   * positive (`isChecked`), and already has property tests over colliding replicas; a bare
   * monotonic number would have needed a merge rule that could not be reversed by a local
   * correction, which is `ListItem.check`'s own historical defect ("the app undid their
   * correction every time, forever, with no way to win").
   *
   * WHY A WATERMARK ON THE LIST AND NOT A MARK PER ITEM. Three reasons, in order of force:
   *
   *   1. NO ITEM IS WRITTEN, so "nothing in your history changed" is a property of the code
   *      rather than a promise in a comment. Every cleared item keeps its `check`, its `closed`
   *      terminal, its stones and its events, untouched. CLAUDE.md's append-only law is not
   *      merely respected here, it is unreachable.
   *   2. THE UNDO CANNOT BE PARTIAL. One scalar goes back; 247 per-item marks have 247 ways to
   *      half-succeed, and an undo that restores 240 of 247 is not an undo.
   *   3. IT IS NOT A REMOVAL, AND MUST NOT BECOME ONE. `R135` purges a removed loop's content
   *      and media after thirty days and states its own scope as a prohibition on generalising
   *      that purge. Routing a weekly clear-out through the removal path would make time destroy
   *      the record of every shop the user has ever done — the append-only law broken by the very
   *      mechanism chosen to look like it was being respected.
   *
   * AN UNCHECK BEATS THE WATERMARK, FOR FREE AND CORRECTLY. Put-away-ness is DERIVED from the
   * item's check mark, never stamped on the item, so an item that is not checked is not put away —
   * whoever cleared, and whenever. An uncheck arriving from a partner's replica therefore returns
   * the item to the list: somebody deliberately said "we do not have this", and that answer wins
   * over a tidy-up performed before they said it.
   *
   * WHOSE UNCHECK, PRECISELY — because this used to say "on this device or a partner's" and the
   * first half was false. A put-away item is drawn on no surface, so there is no row on THIS device
   * to uncheck; the property is exercised by a merge, and by nothing a local user can press. What
   * gives the local user a route back is `restoreClearedItems`, reachable for as long as the
   * watermark stands (`ListSurface`'s standing "put them back" control) rather than only in the
   * moment after the clear. Both halves of that were wrong in the first version of this feature —
   * the claim here, and an undo that lived in component state and died when the screen unmounted —
   * and the `list-clear` critic pass found them together, which is the right way round: an
   * unreachable correction is exactly what `Trolley` exists to prevent ("an uncheck you cannot
   * reach is not a feature") and what `R127` means by checked items staying reachable.
   *
   * A CONSEQUENCE WORTH NAMING: a watermark dated in the future — reachable only by merging with a
   * replica whose clock is wrong — puts away every item checked before it, including ones checked
   * afterwards in real time, and `clearCheckedItems` is then a no-op because nothing reads as
   * checked. This is the one respect in which the pair is NOT symmetric with
   * `checkItem`/`uncheckItem`, whose marks are bounded to one item's own counter-mark rather than
   * compared against every future check. There is no clock in this module to clamp against, so the
   * answer is the escape hatch rather than an impossible validation: `restoreClearedItems` is
   * offered whenever `clearedThrough` is defined, which is precisely that state.
   *
   * NOTHING HERE IS EVER SET BY THE APP. No schedule, no threshold, no trip boundary, no launch
   * sweep, no "you have not shopped in a fortnight". The instant a clear acquires a trigger, the
   * trigger is time and CLAUDE.md's law is broken. `clearCheckedItems` is called from exactly one
   * gesture and there is no other caller.
   *
   * `by` IS NEVER WRITTEN TODAY, on either mark, and that is worth saying rather than leaving as a
   * silence. `Repository.clearCheckedItems(listId, now)` takes no actor, so `laterMark`'s `by`
   * tie-break — which this pair's merge leans on to be total — is inert in the product. It is the
   * gap `toCoreItem` already has for `check.by`, inherited rather than newly introduced, and it
   * costs nothing while one device is the only writer. Whoever gives a shared list real actor
   * identities owes both.
   *
   * NO METRIC IS DERIVED FROM EITHER MARK. Not a shops-cleared count, not a cadence, not a rate.
   * A weekly rhythm the app noticed and reported back would be a streak assembled by accident
   * (design-direction §6 objection 1), on the one axis product-concept §11 forbids outright.
   */
  readonly cleared?: ActorMark;
  readonly uncleared?: ActorMark;
};

/**
 * The section order a new list starts with.
 *
 * A shopping list gets the twelve, in supermarket-walk order, because that is §8.2's whole
 * premise — the user rearranges a sensible default rather than building an aisle map from
 * nothing. A plain list gets ONLY the catch-all: Carn does not know whether your sections are
 * people, rooms or bags, and guessing would be the "AI never acts silently" violation §7.2
 * warns about, in a place where it is not even AI, just presumption.
 */
export function defaultSectionOrder(kind: ListKind): readonly SectionId[] {
  return kind === 'shopping' ? [...BUILT_IN_SECTIONS] : [UNSORTED_SECTION];
}

export function makeList(args: {
  id: string;
  name: string;
  kind: ListKind;
  now: number;
  shared?: boolean;
}): CarnList {
  return {
    id: args.id,
    name: args.name.trim(),
    kind: args.kind,
    sectionOrder: defaultSectionOrder(args.kind),
    sections: [],
    shared: args.shared ?? false,
    memberIds: [],
    createdAt: args.now,
    updatedAt: args.now,
  };
}

export function renameList(list: CarnList, name: string, now: number): CarnList {
  const next = name.trim();
  if (next === '' || next === list.name) return list;
  return { ...list, name: next, updatedAt: Math.max(now, list.updatedAt + 1) };
}

/** Archived, not deleted (R130). Idempotent: the first archiving is the fact. */
export function archiveList(list: CarnList, now: number): CarnList {
  return { ...list, archivedAt: list.archivedAt ?? now };
}

/**
 * The effective clear watermark, or `undefined` when nothing is put away (LC1).
 *
 * A TIE RESOLVES TO UNCLEARED — the correction wins. This is `ListItem.uncheck`'s rule, not
 * `isChecked`'s, and the distinction matters because the first version of this function got it
 * backwards on a borrowed argument.
 *
 * That version preferred CLEARED, reasoning by analogy with `isChecked` ("a false 'checked' costs
 * one glance at a shelf, a false 'unchecked' costs a forgotten item, and the shelf is cheaper") and
 * claiming a tie was "not reachable from the product at all". The `list-clear` critic pass proved
 * it reachable in two ordinary acts, with a test: device A clears at t=100; device B, holding a
 * clear from t=99, presses "put them back", whose clock-forcing lands its countermark at exactly
 * 100. Both merge orders then resolved to CLEARED, so B pressed the only route back and it did
 * nothing, for ever — which is `ListItem.check`'s historical defect ("the app undid their
 * correction every time, forever, with no way to win") reproduced in a new field.
 *
 * The borrowed analogy was also backwards on its own terms: `isChecked` prefers the reading that
 * costs a glance over the one that costs a forgotten item, and here the tidier reading is the one
 * that costs the user a deliberate correction. So: a countermark at or after the mark cancels it.
 */
export function clearedThrough(list: CarnList): number | undefined {
  if (!list.cleared) return undefined;
  if (list.uncleared && list.uncleared.at >= list.cleared.at) return undefined;
  return list.cleared.at;
}

/**
 * Is this item PUT AWAY — cleared off the list, without being removed from it (LC1)?
 *
 * DERIVED FROM THE ITEM'S OWN CHECK MARK, which is the property that makes an uncheck win: an
 * item that is not checked is never put away, whoever cleared and whenever. See `CarnList.cleared`.
 *
 * A put-away item is NOT a removed one and the two must never be conflated. `removedAt` is a
 * tombstone the user set on one thing they did not want (`D32`) and it feeds the thirty-day purge
 * (`R135`); this is a list saying "that shop is finished" about things it still holds in full.
 */
export function isPutAway(list: CarnList, item: ListItem): boolean {
  const through = clearedThrough(list);
  if (through === undefined) return false;
  if (!isChecked(item)) return false;
  // `check` is necessarily defined when `isChecked` is true; the fallback is for the type only.
  return (item.check?.at ?? 0) <= through;
}

/**
 * The items this list actually holds: not removed, not put away.
 *
 * ONE PLACE, because the `removedAt === undefined` filter was already written out in seven
 * separate files before this function existed, and a put-away filter copied to seven places is
 * `R125`'s whole species — a rule stated once and weakened in each restatement.
 */
export function activeItems(list: CarnList, items: readonly ListItem[]): ListItem[] {
  return items.filter((i) => i.removedAt === undefined && !isPutAway(list, i));
}

/**
 * What is in the trolley: checked, still on the list, not cleared away.
 *
 * The set `Trolley` renders and the set the clear acts on, so the count in the doorway and the
 * count in the confirmation cannot disagree.
 */
export function checkedItems(list: CarnList, items: readonly ListItem[]): ListItem[] {
  return activeItems(list, items).filter(isChecked);
}

/**
 * Clear the checked items off the list (LC1). Nothing is deleted and no item is written.
 *
 * IDEMPOTENT BY OBJECT IDENTITY, which `RepositoryEngine.mutateList` depends on: every core
 * transition in this file "returns the SAME OBJECT when the gesture is a no-op", and that is how
 * a clear with nothing to clear stages no outbox entry and wakes no surface.
 *
 * The mark is forced strictly past any existing `uncleared`, the same clock-forcing rule
 * `checkItem` uses and for the same reason: the user's gesture must win locally even if this
 * device's clock is behind the one that undid the last clear.
 *
 * `items` is taken only to decide whether there is anything to do. It is not written, not
 * copied, and not read for anything else — see `CarnList.cleared`, reason 1.
 */
export function clearCheckedItems(
  list: CarnList,
  items: readonly ListItem[],
  now: number,
  by?: string,
): CarnList {
  if (checkedItems(list, items).length === 0) return list;
  const at = Math.max(now, (list.uncleared?.at ?? 0) + 1);
  return { ...list, cleared: { at, by } };
}

/**
 * Put them back — the undo (LC1).
 *
 * Total rather than incremental: it cancels the watermark outright, so everything ever cleared on
 * this list returns. That is a deliberate simplification of a rarer case (a user who cleared twice
 * and wants only the second clear back), taken because the alternative is a stack of watermarks
 * whose merge nobody can reason about, and because this undo is offered in the panel that
 * performed the clear, seconds after it, where "put it back" means the thing that just happened.
 *
 * IDEMPOTENT ON THE MARK, NOT ON THE EFFECT, and the distinction is the honest description. It
 * returns the same object when `clearedThrough` is undefined, which is what `mutateList` needs; it
 * still writes when a watermark stands over items that are all checked after it, i.e. when nothing
 * is actually put away. Tightening that would mean passing the items in and comparing two derived
 * sets to save a no-op write on a state the user cannot see, which buys less than it costs.
 *
 * KIND IS NOT CHECKED HERE, unlike `attachListStore` twenty lines up, whose stated principle is
 * that "the refusal is core's rather than the screen's so a second surface cannot grant one by
 * forgetting to check". The divergence is deliberate: `R126` makes a store a property of a
 * `shopping` list, whereas whether a `plain` list should be clearable is an OPEN question for the
 * ND co-design panel (the last entry in docs/OPEN-QUESTIONS.md). Core stays kind-agnostic so the
 * panel's answer is two conditions on one surface rather than a change here; `ListSurface` is where
 * the `shopping`-only scope is enforced today.
 */
export function restoreClearedItems(list: CarnList, now: number, by?: string): CarnList {
  if (clearedThrough(list) === undefined) return list;
  const at = Math.max(now, (list.cleared?.at ?? 0) + 1);
  return { ...list, uncleared: { at, by } };
}

export function isArchived(list: CarnList): boolean {
  return list.archivedAt !== undefined;
}

/**
 * BIND THIS LIST TO ONE OF THE USER'S REGISTERED PLACES — the write `storePlaceId` was
 * declared for and never had.
 *
 * Authorised by product-concept §8.1 (place is the headline surfacing axis), §8.2 ("the
 * user's regular store"), and RULINGS R129, which fixes "rename, sections, **store**, share
 * and archive" as the five acts that live on a list's own surface. The field above has
 * carried the doc comment "the store this list's geofence is attached to" since R124; until
 * this function existed nothing in the product could put anything in it, so a user could
 * name their shops and never say which list belonged to which — see
 * `apps/mobile/src/screens/shopping/StoreAttachRow.tsx` for the surface, and
 * `docs/DEVIATIONS.md` D40, which moved store LOCATIONS into settings and left the binding
 * unbuilt.
 *
 * TWO FIELDS, AND THEY ARE NOT REDUNDANT. `storePlaceId` is the binding — an opaque id that
 * matches a `Place` in the user's settings and, through that place's `regionRef`, an OS
 * region registration. `storeLabel` is what to CALL it, copied at the moment of binding so a
 * surface can say "near Big Tesco" without holding the settings blob. Both are copied
 * verbatim by `mergeList` and both are already in `listTieKey`, so binding on one device and
 * renaming on another converge the same way a rename does.
 *
 * NO COORDINATE, BY LAW (implementation-brief §3). A `Place` carries none, so neither can
 * this: the id is a uuid the device minted and the label is the word the user typed.
 *
 * `shopping` ONLY (R126): "a plain list starts with no sections, offers no geofence, has no
 * trip." A gift list cannot be given a shop, and the refusal is core's rather than the
 * screen's so a second surface cannot grant one by forgetting to check.
 */
export function attachListStore(
  list: CarnList,
  store: { readonly placeId: string; readonly label: string },
  now: number,
): CarnList {
  if (list.kind !== 'shopping') return list;
  const placeId = store.placeId.trim();
  const label = store.label.trim();
  if (placeId === '') return list;
  // Same no-op discipline as every transition in this file: re-binding the same shop under
  // the same name is not a write, and an outbox entry that changed nothing is a merge
  // conflict waiting to happen on a shared list.
  if (list.storePlaceId === placeId && list.storeLabel === label) return list;
  return {
    ...list,
    storePlaceId: placeId,
    storeLabel: label,
    updatedAt: Math.max(now, list.updatedAt + 1),
  };
}

/**
 * Unbind the shop. NOT a deletion of anything (R130): the place keeps its name and its
 * location in settings, and every item on the list is untouched — the only thing that
 * changes is whether arriving at that shop says anything about this list.
 */
export function detachListStore(list: CarnList, now: number): CarnList {
  if (list.storePlaceId === undefined && list.storeLabel === undefined) return list;
  const { storePlaceId: _placeId, storeLabel: _label, ...rest } = list;
  return { ...rest, updatedAt: Math.max(now, list.updatedAt + 1) };
}

/**
 * The lists that count against the free tier's one-list allowance, and the lists the index
 * shows. One function so those two answers can never disagree — R130 makes "an archived list
 * does not consume the allowance" a deliberate, generous decision, and a second call site
 * computing it differently is how a deliberate decision becomes a bug.
 */
export function liveLists(lists: readonly CarnList[]): CarnList[] {
  return lists.filter((list) => !isArchived(list));
}

// ---------------------------------------------------------------------------
// Section management
// ---------------------------------------------------------------------------

/**
 * Add a section the user authored, to the END of the order.
 *
 * The end rather than a guessed position: the user is about to rearrange it if they care,
 * and inserting it somewhere clever is the "smart rearrangement" design-direction §4 rules
 * out. A duplicate id is a no-op rather than an error — same posture as `checkItem`.
 */
export function addSection(list: CarnList, section: Section, now: number): CarnList {
  if (list.sections.some((s) => s.id === section.id)) return list;
  return {
    ...list,
    sections: [...list.sections, section],
    sectionOrder: list.sectionOrder.includes(section.id)
      ? list.sectionOrder
      : [...list.sectionOrder, section.id],
    updatedAt: Math.max(now, list.updatedAt + 1),
  };
}

/**
 * Rename an authored section.
 *
 * A built-in section CANNOT be renamed, and returns the list unchanged. That is not a
 * limitation to apologise for: `dept.frozen` is a catalogue string in two registers, and
 * letting a user overwrite one would put user content where the copy laws apply and break
 * literal mode's 100%-coverage clause (brief §11). A user who wants their own "Frozen"
 * authors one — it gets a fresh id (`CUSTOM_SECTION_PREFIX`) and behaves like any other.
 */
export function renameSection(
  list: CarnList,
  id: SectionId,
  label: string,
  now: number,
): CarnList {
  const next = label.trim();
  if (next === '') return list;
  let changed = false;
  const sections = list.sections.map((section) => {
    if (section.id !== id || section.label === next) return section;
    changed = true;
    return { ...section, label: next, updatedAt: Math.max(now, section.updatedAt + 1) };
  });
  if (!changed) return list;
  return { ...list, sections, updatedAt: Math.max(now, list.updatedAt + 1) };
}

/**
 * Remove a section. Tombstoned, never deleted (R130), and ITEMS ARE NEVER TAKEN WITH IT.
 *
 * The items that were in it keep their section id and therefore fall through `asSectionId`
 * into the catch-all, where `listGroups` always renders them. That is the single most
 * important behaviour in this function: removing "Mum" from a gift list must never be a way
 * to lose four gift ideas, and a user who removes a section by mistake loses an arrangement,
 * not their content.
 *
 * The catch-all itself cannot be removed — it is where everything else lands.
 */
export function removeSection(list: CarnList, id: SectionId, now: number): CarnList {
  if (id === UNSORTED_SECTION) return list;
  const known = list.sections.some((s) => s.id === id && s.removedAt === undefined);
  const ordered = list.sectionOrder.includes(id);
  if (!known && !ordered) return list;
  return {
    ...list,
    sections: list.sections.map((section) =>
      section.id === id ? { ...section, removedAt: section.removedAt ?? now } : section,
    ),
    sectionOrder: list.sectionOrder.filter((sectionId) => sectionId !== id),
    updatedAt: Math.max(now, list.updatedAt + 1),
  };
}

/**
 * Reorder sections. One gesture, persisted, and that is the whole feature (§8.2).
 * Unknown or missing sections are appended rather than dropped, so a future default
 * set gaining a section cannot silently hide the user's items.
 */
export function reorderSections(
  current: readonly SectionId[],
  next: readonly SectionId[],
): SectionId[] {
  const seen = new Set(next);
  return [...next, ...current.filter((d) => !seen.has(d))];
}

/**
 * Move one section one place up or down — what the two move controls commit.
 *
 * NOT the whole reorder any more. R129 said "buttons and NOT a drag"; docs/DEVIATIONS.md D41
 * overturned that clause on its own grounds and the section manager now carries a drag handle
 * BESIDE the buttons (RULINGS R14: "a hold is never a control's only input"). A drag lands an
 * arbitrary permutation rather than a single swap, so it commits through `reorderSections` above
 * instead. Both routes end in one local write and neither learns anything.
 */
export function moveSection(
  order: readonly SectionId[],
  id: SectionId,
  direction: -1 | 1,
): SectionId[] {
  const from = order.indexOf(id);
  if (from < 0) return [...order];
  const to = from + direction;
  if (to < 0 || to >= order.length) return [...order];
  const next = [...order];
  const moved = next[from]!;
  next[from] = next[to]!;
  next[to] = moved;
  return next;
}

/** The live (non-tombstoned) authored section definitions, by id. */
export function sectionsById(list: CarnList): Map<SectionId, Section> {
  const map = new Map<SectionId, Section>();
  for (const section of list.sections) {
    if (section.removedAt === undefined) map.set(section.id, section);
  }
  return map;
}

/**
 * Every section id this list currently arranges, in the user's order.
 *
 * A LIVE AUTHORED SECTION MISSING FROM `sectionOrder` IS APPENDED HERE, at read time, and this
 * is the only place that repair happens (R125 clause 5's instinct, applied to sections rather
 * than to items). `mergeList` used to do it, and doing it there cost the merge its associativity:
 * the appended tail became part of the next merge's tie-break input, so three replicas tying on
 * `updatedAt` converged on different orders depending on which pair merged first. See
 * `mergeList` for the whole argument.
 *
 * Read time is also the more thorough place. It repairs a list however it got into that state —
 * a row written by an older build, a hand-edited database, a payload from a client that has not
 * shipped yet — not only a list that has just been through this module's merge.
 *
 * Tombstoned sections are NOT re-appended: `removeSection` takes the id out of the order and
 * stamps `removedAt`, and undoing half of that on every read would make removal impossible.
 */
export function sectionIds(list: CarnList): readonly SectionId[] {
  /**
   * THE REPAIR IS SYMMETRIC, and it was not.
   *
   * This function used to refuse to APPEND a tombstoned section while happily returning one that
   * was already in `sectionOrder`, which made the tombstone a suggestion rather than a fact. Two
   * ordinary sequences put it back:
   *
   *   - a merge. `mergeList` takes the winner's `sectionOrder` verbatim (deliberately — an order
   *     is one user intention, not twelve independent facts), so a partner's later list update
   *     restores an order containing the id this device removed;
   *   - a re-save. Any later write that projects the list back through the app layer carries
   *     `sectionOrder` with it.
   *
   * The result was worse than a stale heading. A removed authored section is tombstoned, so
   * `sectionsById` skips it and `sectionName` has no label to resolve — the heading painted the
   * RAW MINTED ID, `sec-3f9a…`, and `namedSections` reported `authored: false`, so the section
   * manager offered neither rename nor remove. The user could not undo it by any path in the app.
   *
   * Removing the id here rather than in the merge keeps R131's rule: the repair lives at read
   * time, where it is total — it fixes any list however it came to be that way — and where it
   * cannot feed back into the merge algebra and break associativity again.
   *
   * The items are not affected either way. An id that is not in this list falls through
   * `listGroups`' rank check into the catch-all, which is always rendered: removing a section
   * costs an arrangement, never content.
   */
  const tombstoned = new Set<SectionId>(
    list.sections.filter((section) => section.removedAt !== undefined).map((section) => section.id),
  );
  const order = list.sectionOrder.filter((id) => !tombstoned.has(id));
  const seen = new Set<SectionId>(order);
  for (const section of list.sections) {
    if (section.removedAt !== undefined || seen.has(section.id)) continue;
    seen.add(section.id);
    order.push(section.id);
  }
  return order.length > 0 ? order : [UNSORTED_SECTION];
}

// ---------------------------------------------------------------------------
// Presentation
// ---------------------------------------------------------------------------

export type SectionGroup = {
  readonly section: SectionId;
  readonly items: readonly ListItem[];
};

/**
 * Group a list's items into its sections, in the user's manual order.
 *
 * THE ONLY GROUPING FUNCTION IN THE PRODUCT. Both the resting surface and shopping mode read
 * it, so "checked items vanish" and the manual section order are decided in one place — a
 * list that dimmed checked rows on one surface and hid them on the other would be two
 * different products.
 *
 * `includeChecked` is the one axis they differ on, and R127 is the ruling that says so:
 * §8.2's "checked items vanish" is one sentence about the MODE, and every clause after its
 * semicolon ("the list ends; the mode congratulates and closes") is about a trip. A `plain`
 * list has no trip at all, and an implementer reading that clause as a law about lists would
 * build a gift list that eats the gift the moment you buy it, three weeks before you give it.
 *
 * ORDER COMES FROM TWO INPUTS AND NOTHING ELSE: the user's `sectionOrder` between sections,
 * and `addedAt` within one. Not from check times, not from how the last trip was worked, not
 * from anything derived from either. See this file's header and docs/DEVIATIONS.md D21 —
 * `packages/core/test/lists.test.ts` asserts the output is invariant under any permutation of
 * the items' check/uncheck timestamps, and it fails the moment anything sorts by one.
 *
 * EMPTY SECTIONS ARE RENDERED WHEN `includeEmpty` IS SET, and that is not cosmetic: on a gift
 * list "Dad" with nothing in it yet is the user's own structure, and hiding it would mean a
 * section vanished the moment they made it. On a trip it is the opposite — a supermarket
 * department with nothing to buy in it is noise between you and the next thing in your basket.
 * A user may now ask for the trip's reading at rest too (`Settings.showEmptySections`, RULINGS
 * R132) — the default is unchanged, and `restingGroups` below is the only thing that passes it.
 */
export function listGroups(
  list: CarnList,
  items: readonly ListItem[],
  options?: { includeChecked?: boolean; includeEmpty?: boolean },
): SectionGroup[] {
  const includeChecked = options?.includeChecked ?? false;
  const includeEmpty = options?.includeEmpty ?? false;

  // `activeItems` drops the removed AND the put-away (LC1), so a cleared shop leaves every
  // grouping in the app at once — the resting surface, the mode, the widget and the index row —
  // rather than each of them learning the new rule separately.
  const live = activeItems(list, items).filter((i) => includeChecked || !isChecked(i));
  const order = sectionIds(list);
  const rank = new Map<SectionId, number>(order.map((d, i) => [d, i]));

  const bySection = new Map<SectionId, ListItem[]>();
  if (includeEmpty) for (const id of order) bySection.set(id, []);

  for (const item of live) {
    const section = rank.has(item.section) ? item.section : UNSORTED_SECTION;
    const bucket = bySection.get(section);
    if (bucket) bucket.push(item);
    else bySection.set(section, [item]);
  }

  return [...bySection.entries()]
    .filter(([, group]) => includeEmpty || group.length > 0)
    .sort(([a], [b]) => (rank.get(a) ?? order.length) - (rank.get(b) ?? order.length))
    .map(([section, group]) => ({
      section,
      // Within a section, oldest-captured first. Stable, and NOT learned.
      items: [...group].sort((x, y) => x.addedAt - y.addedAt),
    }));
}

/**
 * The shopping-mode view: "a high-contrast, huge-tap-target, one-handed checklist —
 * nothing else on screen. Items are grouped by department... Checked items vanish;
 * the list ends" (§8.2).
 *
 * Checked items are excluded rather than dimmed, because "vanish" is the specified
 * behaviour and because a screen of struck-through rows is the shopping equivalent of
 * the backlog wall. Empty sections are excluded for the same reason.
 */
export function shoppingModeGroups(list: CarnList, items: readonly ListItem[]): SectionGroup[] {
  return listGroups(list, items, { includeChecked: false, includeEmpty: false });
}

/**
 * The resting view: the list as you glance at it at home.
 *
 * Keeps checked items in place and keeps empty sections visible (R127, and `listGroups`'
 * own note on why an empty "Dad" must not disappear). A `shopping` list's resting surface
 * still puts checked items in the trolley rather than in the groups — that is the SURFACE's
 * composition, not this function's job, and `ShoppingListSurface` has done it that way since
 * before R127 named the rule.
 *
 * `showEmptySections` IS THE USER'S SETTING, PASSED IN, AND ITS ABSENCE IS THE SHIPPED
 * DEFAULT (RULINGS R132, `Settings.showEmptySections`). It is a parameter rather than a
 * branch a surface takes for itself, because which booleans "at rest" means is this
 * function's answer and a caller that reached for `listGroups` directly to drop the empty
 * ones would be re-deciding `includeChecked` on the way past — the exact second grouping
 * `useShoppingList`'s docblock exists to prevent. The kind-dependent half stays here; the
 * user-dependent half arrives here.
 */
export function restingGroups(
  list: CarnList,
  items: readonly ListItem[],
  preferences?: { showEmptySections?: boolean },
): SectionGroup[] {
  return listGroups(list, items, {
    includeChecked: list.kind === 'plain',
    includeEmpty: preferences?.showEmptySections ?? true,
  });
}

/**
 * The list ends, the mode congratulates and closes (§8.2).
 *
 * A PREDICATE OVER THE ITEMS IT IS GIVEN, so a caller holding a list that has been cleared must
 * hand it `activeItems(list, items)` (LC1). Left to itself this function would count put-away
 * items as live-and-checked and call a list with nothing on it "complete" — true of the set,
 * false of the list, and the wrong one of those to hand a surface. Kept as a signature over items
 * rather than over a list because the shared-list web fallback (`apps/web/src/list`) reaches it
 * with a set it assembled itself and no `CarnList` to pass.
 */
export function shoppingTripComplete(items: readonly ListItem[]): boolean {
  const live = items.filter((i) => i.removedAt === undefined);
  return live.length > 0 && live.every((i) => isChecked(i));
}

export function remainingCount(items: readonly ListItem[]): number {
  return items.filter((i) => i.removedAt === undefined && !isChecked(i)).length;
}

/**
 * Trip completion metric (product-concept §13: "% of trips where the list is fully
 * worked"). Computed per trip from the items present when the trip started, so adding
 * an item mid-shop cannot retroactively mark a good trip incomplete.
 *
 * An empty trip returns `undefined`, not 1 (critique 6.6): walking into a shop with
 * nothing on the list is not a perfect score, it is not a trip.
 */
export function tripCompletionRate(args: {
  itemsAtStart: readonly string[];
  /**
   * MEMBERSHIP ONLY — which is why the type is an iterable and not an array.
   *
   * The caller's snapshot (`stores/shoppingSession.ts`) is now a `Set`, because the shopping-mode
   * surface's stated defence against route learning was "the trip snapshot... is a set — it has
   * no order in it to learn from" and it was a `readonly string[]` that `noteChecked` APPENDED
   * to, i.e. an array in check-off order. The practical risk was small (it is ephemeral, cleared
   * on exit) but the defence offered against a founder-level prohibition was a false statement
   * about a data structure. This function only ever asked "is this id in there", so the honest
   * shape costs nothing.
   */
  itemsCheckedDuringTrip: Iterable<string>;
}): number | undefined {
  if (args.itemsAtStart.length === 0) return undefined;
  const checked = new Set(args.itemsCheckedDuringTrip);
  const worked = args.itemsAtStart.filter((id) => checked.has(id)).length;
  return worked / args.itemsAtStart.length;
}

// ---------------------------------------------------------------------------
// Merging lists
// ---------------------------------------------------------------------------

/**
 * Merge two views of the same authored section.
 *
 * Same shape of join as `mergeItem`, for the same reason (brief §2): per-field LWW on
 * `updatedAt` with a total tie-break, earliest `addedAt`, earliest non-undefined
 * `removedAt`. Commutative, associative, idempotent.
 */
export function mergeSection(a: Section, b: Section): Section {
  const winner =
    a.updatedAt !== b.updatedAt
      ? a.updatedAt > b.updatedAt
        ? a
        : b
      : a.label >= b.label
        ? a
        : b;
  return {
    ...winner,
    addedAt: Math.min(a.addedAt, b.addedAt),
    updatedAt: Math.max(a.updatedAt, b.updatedAt),
    removedAt: earliest(a.removedAt, b.removedAt),
  };
}

/**
 * Merge two views of the same list.
 *
 * `sections` is a union by id, so a section authored on one device and one authored on
 * another BOTH survive — the same guarantee `mergeItems` gives items, and for the same
 * reason: two people adding to a shared list at once is the normal case, not the edge one.
 *
 * `sectionOrder` is whole-value LWW rather than a per-position merge, which is a deliberate
 * choice and worth stating. An order is a single user intention ("this is my aisle walk"),
 * not twelve independent facts; merging two orders position-by-position produces an
 * arrangement neither person chose, which is worse than losing the older of two deliberate
 * acts. It is taken from the winner VERBATIM; a live authored section the winning order does
 * not mention is restored by `sectionIds` at read time, which is where that repair now lives —
 * see below, and see `sectionIds`.
 *
 * `archivedAt` is earliest-non-undefined: a tombstone, once set, is a fact (R130).
 *
 * WHY THE WINNER IS PICKED THE WAY IT IS, because two earlier attempts at this line were
 * wrong and both failures are instructive. `packages/core/test/lists.test.ts` asserts all
 * three properties over generated replicas, which brief §2 mandates ("property-based conflict
 * tests") and which is the only reason the two defects below were visible at all:
 *
 *   1. The tie-break was `a.id >= b.id ? a : b`. Two views of the SAME list always carry the
 *      same id, so that expression is always `a` — i.e. no tie-break at all, and two replicas
 *      tying on `updatedAt` but disagreeing on `name` or `sectionOrder` converged on whichever
 *      one the caller happened to pass first. `mergeItem` had the identical bug against
 *      `checkedBy` and fixed it with `itemTieKey`; `listTieKey` below is that fix, applied to
 *      every field this function copies from the winner.
 *   2. Any live section missing from the winning order used to be APPENDED here. That is a
 *      correct repair in the wrong place: the appended tail is part of `listTieKey`'s input,
 *      so `mergeList(mergeList(a, b), c)` and `mergeList(a, mergeList(b, c))` picked different
 *      winners whenever the three tied on `updatedAt` and a loser contributed a section — the
 *      merge stopped being associative, so two devices could converge on two different orders
 *      and stay there. The repair moved to `sectionIds`, at read time, where it cannot feed
 *      back into the algebra. Nothing is hidden either way: `listGroups` renders an item whose
 *      section is unknown in the catch-all, always.
 *
 * `sections` is sorted by (`addedAt`, `id`) rather than left in the union's insertion order,
 * for the same reason: insertion order is argument order, and an array that differs by argument
 * order is not a commutative result even when it holds the same sections.
 */
export function mergeList(a: CarnList, b: CarnList): CarnList {
  const winner = laterListUpdate(a, b);

  const byId = new Map<SectionId, Section>();
  for (const section of a.sections) byId.set(section.id, section);
  for (const section of b.sections) {
    const existing = byId.get(section.id);
    byId.set(section.id, existing ? mergeSection(existing, section) : section);
  }
  // Oldest-authored first, `id` as the total tie-break. A function of the SET, not of the order
  // the two argument arrays happened to be walked in.
  const sections = [...byId.values()].sort(
    (x, y) => x.addedAt - y.addedAt || (x.id < y.id ? -1 : x.id > y.id ? 1 : 0),
  );

  return {
    ...winner,
    sections,
    memberIds: [...new Set([...a.memberIds, ...b.memberIds])].sort(),
    shared: a.shared || b.shared,
    createdAt: Math.min(a.createdAt, b.createdAt),
    updatedAt: Math.max(a.updatedAt, b.updatedAt),
    archivedAt: earliest(a.archivedAt, b.archivedAt),
    /**
     * The clear watermark and its countermark, joined LATEST-WINS PER MARK (LC1) — the identical
     * rule `mergeItem` applies to `check`/`uncheck`, so the two pairs cannot drift apart.
     *
     * NEITHER MARK MAY EVER ENTER `listTieKey`, and this is load-bearing rather than tidy.
     * That key's contract is that it covers only fields copied VERBATIM from the winner, because
     * `listTieKey(mergeList(a, b)) === listTieKey(winner)` is what makes "max by (updatedAt, key)"
     * associative. These two are RECOMPUTED here, so putting them in the key would cost
     * associativity exactly as appending the section tail did — `mergeList`'s note 2, which is the
     * defect that let two devices converge on two different orders and stay there.
     *
     * `laterMark` is total and symmetric on its own (it ties on `at`, then on `by`), so this join
     * is commutative, associative and idempotent independently of the winner selection above.
     */
    cleared: laterMark(a.cleared, b.cleared),
    uncleared: laterMark(a.uncleared, b.uncleared),
  };
}

function laterListUpdate(a: CarnList, b: CarnList): CarnList {
  if (a.updatedAt !== b.updatedAt) return a.updatedAt > b.updatedAt ? a : b;
  const ka = listTieKey(a);
  const kb = listTieKey(b);
  if (ka !== kb) return ka > kb ? a : b;
  return a;
}

/**
 * A total, symmetric ordering key over EVERY field `mergeList` copies from the winner —
 * exactly the contract `itemTieKey` states for items, and for the same reason: when two
 * replicas tie on `updatedAt` and on this key, the merge falls back to "either one", and that
 * is only sound if the fields it copies are identical.
 *
 * Every field here is copied VERBATIM from the winner and none of them is recomputed by the
 * merge, which is what makes `listTieKey(mergeList(a, b)) === listTieKey(winner)` — and that
 * identity is what makes "max by (updatedAt, key)" associative rather than merely commutative.
 * Adding a field that the merge derives rather than copies would silently cost associativity
 * again; see `mergeList`'s note 2.
 */
function listTieKey(list: CarnList): string {
  return JSON.stringify([
    list.id,
    list.name,
    list.kind,
    [...list.sectionOrder],
    list.storePlaceId ?? null,
    list.storeLabel ?? null,
  ]);
}
